import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Phone, ArrowRight, Eye, EyeOff, AlertCircle, CheckCircle, Sparkles, Shield, Clock, Home, MessageCircle } from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';

const ClientAuth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const { login, register } = useClientAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Get region from URL
  const getBasePath = () => {
    if (location.pathname.startsWith('/ie')) return '/ie';
    if (location.pathname.startsWith('/pt')) return '/pt';
    return '/pt';
  };
  const basePath = getBasePath();
  const isIreland = basePath === '/ie';

  const getText = (en, pt) => language === 'pt' ? pt : en;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        setSuccess(getText('Login successful! Redirecting...', 'Login bem sucedido! A redirecionar...'));
        // Redirect to contact/quote page after login
        setTimeout(() => navigate(`${basePath}/contact`), 1000);
      } else {
        // Validation
        if (formData.password !== formData.confirmPassword) {
          throw new Error(getText('Passwords do not match', 'As passwords não coincidem'));
        }
        if (formData.password.length < 6) {
          throw new Error(getText('Password must be at least 6 characters', 'A password deve ter pelo menos 6 caracteres'));
        }
        
        await register(formData.name, formData.email, formData.phone, formData.password);
        setSuccess(getText('Account created! Redirecting...', 'Conta criada! A redirecionar...'));
        // Redirect to contact/quote page after registration
        setTimeout(() => navigate(`${basePath}/contact`), 1000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Clock className="text-[#d4a84b]" size={24} />,
      title: getText('Track Your Orders', 'Acompanhe Pedidos'),
      desc: getText('View status of all your requests', 'Veja o estado de todos os seus pedidos')
    },
    {
      icon: <Sparkles className="text-[#d4a84b]" size={24} />,
      title: getText('Quick Booking', 'Reserva Rápida'),
      desc: getText('Pre-filled forms, faster checkout', 'Formulários preenchidos, checkout mais rápido')
    },
    {
      icon: <Shield className="text-[#d4a84b]" size={24} />,
      title: getText('Exclusive Updates', 'Atualizações Exclusivas'),
      desc: getText('Get notified about your bookings', 'Receba notificações sobre as suas reservas')
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="client-auth-page">
      {/* Simple Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to={basePath} className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <a 
                href="https://wa.me/353899502499" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-2 text-[#25D366] hover:text-[#20BA5A] font-medium"
              >
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </a>
              <Link 
                to={basePath} 
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Home size={18} />
                <span className="hidden sm:inline">{getText('Home', 'Início')}</span>
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a5c3a] via-[#1a5c3a] to-[#0f3d26] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
              {isLogin 
                ? getText('Welcome to Your Client Area', 'Bem-vindo à Sua Área de Cliente')
                : getText('Join Sancur Today', 'Junte-se à Sancur Hoje')
              }
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
              {isLogin 
                ? getText('Sign in to manage your bookings and track your orders', 'Entre para gerir as suas reservas e acompanhar pedidos')
                : getText('Create your account and enjoy a seamless cleaning experience', 'Crie a sua conta e desfrute de uma experiência de limpeza perfeita')
              }
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="flex-1 py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            
            {/* Benefits Side - Desktop */}
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">
                  {getText('Why Create an Account?', 'Porquê Criar uma Conta?')}
                </h2>
                
                <div className="space-y-4">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="flex gap-4 p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#1a5c3a]/10 rounded-lg flex items-center justify-center">
                        {benefit.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{benefit.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Trust Badge */}
                <div className="mt-8 p-6 bg-gradient-to-r from-[#1a5c3a] to-[#0f3d26] rounded-xl text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield size={28} />
                    <span className="font-semibold text-lg">{getText('Your Data is Safe', 'Os Seus Dados Estão Seguros')}</span>
                  </div>
                  <p className="text-white/80 text-sm">
                    {getText('We use industry-standard encryption to protect your personal information.', 'Utilizamos encriptação padrão da indústria para proteger as suas informações pessoais.')}
                  </p>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                {/* Tab Switcher */}
                <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                  <button
                    onClick={() => { setIsLogin(true); setError(''); setSuccess(''); }}
                    className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                      isLogin 
                        ? 'bg-white text-[#1a5c3a] shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid="tab-login"
                  >
                    {getText('Sign In', 'Entrar')}
                  </button>
                  <button
                    onClick={() => { setIsLogin(false); setError(''); setSuccess(''); }}
                    className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                      !isLogin 
                        ? 'bg-white text-[#1a5c3a] shadow-sm' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    data-testid="tab-register"
                  >
                    {getText('Register', 'Registar')}
                  </button>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                    <AlertCircle size={20} className="flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {success && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
                    <CheckCircle size={20} className="flex-shrink-0" />
                    <span className="text-sm">{success}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('Full Name', 'Nome Completo')} *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required={!isLogin}
                          className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent transition-all"
                          placeholder={getText('Your name', 'O seu nome')}
                          data-testid="name-input"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent transition-all"
                        placeholder="email@example.com"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  {!isLogin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('Phone (optional)', 'Telefone (opcional)')}
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent transition-all"
                          placeholder="+353 89 950 2499"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-12 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent transition-all"
                        placeholder="••••••••"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  {!isLogin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {getText('Confirm Password', 'Confirmar Password')} *
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required={!isLogin}
                          className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent transition-all"
                          placeholder="••••••••"
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-[#1a5c3a] to-[#0f3d26] text-white font-semibold rounded-xl hover:from-[#0f3d26] hover:to-[#1a5c3a] transition-all disabled:opacity-50 shadow-lg hover:shadow-xl mt-6"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        {isLogin ? getText('Sign In', 'Entrar') : getText('Create Account', 'Criar Conta')}
                        <ArrowRight size={20} />
                      </>
                    )}
                  </button>
                </form>

                {/* Mobile Benefits */}
                <div className="lg:hidden mt-8 pt-6 border-t border-gray-200">
                  <p className="text-center text-sm text-gray-600 mb-4">
                    {getText('Why create an account?', 'Porquê criar uma conta?')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock size={16} className="text-[#d4a84b]" />
                      {getText('Track Orders', 'Ver Pedidos')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Sparkles size={16} className="text-[#d4a84b]" />
                      {getText('Quick Booking', 'Reserva Rápida')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-[#1a5c3a] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <div className="flex items-center gap-6 text-white/80 text-sm">
              <a 
                href="https://wa.me/353899502499" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <MessageCircle size={18} />
                <span>+353 89 950 2499</span>
              </a>
              <span>|</span>
              <a href="mailto:suporte@sancurservices.com" className="hover:text-white transition-colors">
                suporte@sancurservices.com
              </a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/20 text-center text-white/60 text-sm">
            © 2026 Sancur Property Services. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientAuth;
